import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Foldout from 'components/shared/Foldout';
import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import ArrowLeft from 'svg/ArrowLeft';
import XIcon2 from 'svg/XIcon2';

// A right-hand slideout shell for inbound-referral detail views (eligibility check today; denial
// / sex-offender detail next). Built on the shared Foldout, it owns the header chrome — back
// arrow, title, optional subtitle, an optional right-aligned action slot, and a close × — and a
// scrolling body. Section-specific content is passed as children.

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  titleBadge?: ReactNode;
  // Right-aligned header slot (e.g. a Download button). Sits left of the close ×.
  headerAction?: ReactNode;
  // The back arrow duplicates the close × — both just close — so surfaces whose design draws
  // only the × can drop it.
  hideBackButton?: boolean;
  // Fixed under the header, above the scrolling body: the coverage tab strip (OLIO-2794). Outside
  // the body on purpose — switching coverage has to stay reachable however far down a long result
  // the reader has scrolled.
  tabs?: ReactNode;
  // Hides the header's own border-bottom — for callers whose tabs (or detailsBar) already draw the
  // separator immediately below them, which otherwise doubles up against this one.
  hideHeaderBorder?: boolean;
  // Fixed under the tabs, above the scrolling body: a row of detail fields tied to whatever the tabs
  // are currently showing (eligibility's Payer Name / Member ID, OLIO-3116). Outside the body for the
  // same reason as tabs — and drawn as its own bordered strip so it reads as scoped to the tab above it
  // rather than as part of the header.
  detailsBar?: ReactNode;
  // Pinned to the bottom, for actions that belong to whatever the body is showing (when it last ran,
  // Download PDF, Re-Run). Same reason as the tabs: a benefits result is long enough that a footer
  // inside the scroll would only be found by reaching the end of it.
  footer?: ReactNode;
  // Puts the back arrow on the title's own line, so the subtitle starts at the header's left edge
  // instead of indented under the title. This is the High Cost Medication Check's header design.
  //
  // Opt-in rather than the default because the original layout — arrow in its own column beside a
  // stacked title+subtitle — is what the Benefit Verification slideout was built and approved
  // against. If design wants this treatment for every detail slideout, flipping the default is a
  // one-line follow-up rather than a silent change to a screen already signed off.
  backInTitle?: boolean;
  children: ReactNode;
}

export default function DetailSlideout({
  open,
  onClose,
  title,
  subtitle,
  titleBadge,
  headerAction,
  hideBackButton,
  tabs,
  hideHeaderBorder,
  detailsBar,
  footer,
  backInTitle = false,
  children,
}: Props) {
  const isMobile = useIsMobile();

  const back = (
    <BackButton type='button' aria-label='Back' onClick={onClose}>
      <ArrowLeft />
    </BackButton>
  );

  return (
    <Foldout open={open} onBackdropClick={onClose} width={isMobile ? '100%' : 'min(95%, 1040px)'}>
      <Container>
        {/* Two header shapes, differing only in what the arrow belongs to. With backInTitle the
            arrow rides on the title line and the subtitle spans the header's full width; without
            it the arrow is its own column and the subtitle stacks under the title. */}
        <HeaderBar $stacked={backInTitle} $hideBorder={hideHeaderBorder}>
          {!backInTitle && !hideBackButton && back}
          <TitleSection data-testid='slideout-title-row'>
            {backInTitle ? (
              <TitleRow>
                {!hideBackButton && back}
                <Title $compact>{title}</Title>
                {titleBadge}
              </TitleRow>
            ) : (
              <>
                <TitleRow>
                  <Title>{title}</Title>
                  {titleBadge}
                </TitleRow>
                {subtitle !== undefined && <Subtitle>{subtitle}</Subtitle>}
              </>
            )}
          </TitleSection>
          <HeaderActions $top={backInTitle}>
            {headerAction}
            <CloseButton $compact={backInTitle} type='button' aria-label='Close' onClick={onClose}>
              <XIcon2 width={12} height={12} />
            </CloseButton>
          </HeaderActions>
        </HeaderBar>
        {backInTitle && subtitle !== undefined && <StandaloneSubtitle>{subtitle}</StandaloneSubtitle>}
        {tabs && <TabBar data-testid='slideout-tabs'>{tabs}</TabBar>}
        {detailsBar && <DetailsBar data-testid='slideout-details-bar'>{detailsBar}</DetailsBar>}
        <Body>{children}</Body>
        {footer && <FooterBar data-testid='slideout-footer'>{footer}</FooterBar>}
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

// Stacked drops the rule and the bottom padding: the standalone subtitle below carries both, so the
// header reads as one block with a single rule under all of it rather than one splitting it.
// $hideBorder drops the same rule for callers whose tabs or detailsBar already draw one below it.
const HeaderBar = styled.div<{ $stacked: boolean; $hideBorder?: boolean }>`
  display: flex;
  align-items: ${({ $stacked }) => ($stacked ? 'flex-start' : 'center')};
  gap: 12px;
  padding: ${({ $stacked }) => ($stacked ? '16px 24px 0' : '16px 24px')};
  ${({ $stacked, $hideBorder }) =>
    !$stacked &&
    !$hideBorder &&
    css`
      border-bottom: 1px solid ${colors.black10};
    `}
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// align-self: flex-start top-aligns the arrow with the title rather than centering it on the
// whole (title + subtitle) block — HeaderBar's own align-items: center still governs the rest.
const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin-left: -8px;
  flex-shrink: 0;
  align-self: flex-start;
  color: ${colors.black100};
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

// $compact is scoped to the backInTitle header, which is the High Cost Medication Check's design:
// smaller than the default. Benefit Verification's approved header keeps its 18px.
const Title = styled.h2<{ $compact?: boolean }>`
  font-size: ${({ $compact }) => ($compact ? '16px' : '18px')};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  margin: 0;
  color: ${colors.black100};
`;

const Subtitle = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.black50};
`;

// The backInTitle subtitle: a sibling of the header row rather than a child of the title column, so
// it starts at the header's left edge (flush under the back arrow) instead of indented under the
// title. It also owns the header's bottom rule.
const StandaloneSubtitle = styled(Subtitle)`
  display: block;
  font-size: 14px;
  padding: 12px 24px 16px;

  border-bottom: 1px solid ${colors.black10};
`;

const HeaderActions = styled.div<{ $top: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  ${({ $top }) =>
    $top &&
    css`
      align-self: flex-start;
    `}
`;

// $compact collapses the button's box to the height of the title beside it. At its default padding
// the button is 20px — the icon plus 8px — which in the stacked header would leave dead space under
// the title that the subtitle's own padding cannot remove.
const CloseButton = styled.button<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
  color: ${colors.black75};
  padding: 4px 8px;

  ${({ $compact }) =>
    $compact &&
    css`
      width: 20px;
      height: 20px;
      padding: 0;
    `}
`;

const TabBar = styled.div`
  padding: 0 24px;
  border-bottom: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const DetailsBar = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  background: ${colors.white};
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  /* Sections keep their natural height so the body scrolls to fit. Without this, a child with
     overflow:hidden gets min-height:0 and the flex column shrinks/clips it to ~0 height. */
  > * {
    flex-shrink: 0;
  }
`;
